import { Form } from '@unistocks/components/form/form';
import {
  createDateSchema,
  createMoreThanSchema,
  createNumberSchema,
  requiredSchema
} from '@unistocks/components/form/validators';
import { Paragraph } from '@unistocks/components/text/text';
import { PortfolioBase } from '@unistocks/features/portfolios/models/portfolio-base';
import { t } from '@unistocks/localization';
import { PortfolioDto, TransactionCreateDto, TransactionType } from '@unistocks/proxy/axios-client';
import { translateEnumOptions } from '@unistocks/shared/utils/enum-utils';
import { transactionTypeOptions } from '@unistocks/shared/utils/enums/transaction-options';
import { createDate, today } from '@unistocks/shared/utils/util';
import { FormPropsBase } from '@unistocks/types/form-props';
import { useMemo } from 'react';
import { z } from 'zod';

export type AddTransactionFormProps = FormPropsBase<TransactionCreateDto> & {
  portfolio: PortfolioDto;
};

const transactionTypeValues = transactionTypeOptions.map(option => option.value);

const schemaFactory = (portfolio: PortfolioBase) =>
  z.object({
    portfolioId: requiredSchema(z.string(), t('::Portfolio')),
    type: requiredSchema(
      createNumberSchema().refine(
        value => value !== null && transactionTypeValues.includes(value),
        t('::ValidationError:Required:Selection', { 0: t('::TransactionType') })
      ),
      t('::TransactionType')
    ),
    date: requiredSchema(
      createDateSchema({
        min: createDate(portfolio.creationTime).toJSDate(),
        max: today().toJSDate()
      }),
      t('::Date')
    ),
    amount: requiredSchema(
      createMoreThanSchema(0, t('::Transaction:Amount')),
      t('::Transaction:Amount')
    ),
    comment: z.string().nullish()
  });

export const AddTransactionForm: React.FC<AddTransactionFormProps> = ({
  portfolio,
  isSubmitting,
  onSubmit
}) => {
  const schema = useMemo(() => schemaFactory(portfolio), [portfolio]);
  const initialValue: Partial<TransactionCreateDto> = {
    portfolioId: portfolio.id,
    type: TransactionType.Deposit,
    date: today().toISODate()
  };

  const transactionMinDate = useMemo(
    () => createDate(portfolio.creationTime).toJSDate(),
    [portfolio]
  );
  const transactionMaxDate = useMemo(() => today().toJSDate(), []);

  return (
    <>
      <Paragraph mb={'$3'}>{t('::AddTransactionDescription')}</Paragraph>

      <Form
        formId={'add-transaction'}
        schema={schema}
        initialValues={initialValue}
        isLoading={isSubmitting}
        triggerText={t('::Add')}
        onSubmit={onSubmit}
      >
        {(form, submit) => (
          <>
            <Form.Select
              name={'type'}
              label={t('::TransactionType')}
              options={translateEnumOptions(transactionTypeOptions)}
              isRequired
            />

            <Form.Datepicker
              name={'date'}
              label={t('::Date')}
              isRequired
              minDate={transactionMinDate}
              maxDate={transactionMaxDate}
            />

            <Form.Input
              name={'amount'}
              label={t('::Transaction:Amount')}
              isRequired
              keyboardType={'numeric'}
              appendText={portfolio.currency}
              onSubmitEditing={() => submit()}
            />
          </>
        )}
      </Form>
    </>
  );
};

AddTransactionForm.displayName = 'AddTransactionForm';
